import { ReactNode } from 'react';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { appliedCapTooltipInputs, InflationTooltipContent } from '../cells';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import type { StrategyPresentation } from './types';

// NYL Asset Flex presentation: standardizes on the shared
// BalanceBreakdownTooltip for pool / death benefit cells; adds a
// reimbursement-rule explanation on the payout cell.

// Renders a static "$Y/mo × Z mo = $X" note for a duration-derived pool.
// Returns undefined for legacy policies where duration is not known.
function poolAtIssueNote(
  baseMonthly: number,
  durationMonths: number | undefined,
  atIssue: number | undefined,
): string | undefined {
  if (durationMonths === undefined || atIssue === undefined) return undefined;
  return `At issue: ${currency(baseMonthly)}/mo × ${durationMonths} mo = ${currency(atIssue)}`;
}

// Footer for the benefit-amount inflation tooltip: links this year's monthly
// cap to the inflated pool ceilings (cap × duration months) so advisors can
// see where the Acceleration / EOB headline dollars come from. Only shown for
// duration-derived policies (legacy policies have no month count to multiply).
function poolCeilingFooter(monthlyCap: number, terms: ClaimTerms): ReactNode | null {
  const accelMonths = terms.baseBenefitPoolDurationMonths;
  const eobMonths = terms.extensionOfBenefitsPoolDurationMonths;
  if (accelMonths === undefined && eobMonths === undefined) return null;
  return (
    <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 text-[10px]">
      <span className="col-span-2 text-gray-300">Pool ceiling at this monthly cap</span>
      {accelMonths !== undefined && (
        <>
          <span className="text-gray-300">Acceleration × {accelMonths} mo</span>
          <span className="font-semibold tabular-nums">{currency(monthlyCap * accelMonths)}</span>
        </>
      )}
      {eobMonths !== undefined && eobMonths > 0 && (
        <>
          <span className="text-gray-300">EOB × {eobMonths} mo</span>
          <span className="font-semibold tabular-nums">{currency(monthlyCap * eobMonths)}</span>
        </>
      )}
    </div>
  );
}

export const nylAssetFlexPresentation: StrategyPresentation = {
  benefitAmountCell: ({ month, terms }) => {
    // Pre-policy rows carry no applied cap; fall through to the default cell
    // (which renders a dash when there's no snapshot for the year).
    if (month.events.ineligibilityReason === 'pre-policy') return null;
    // Headline = the engine's own applied cap for this row; the tooltip's
    // base × (1+r)^n derivation is reconstructed from terms for display only.
    const monthlyCap = month.facts.appliedMonthlyBenefitCap;
    const { baseMonthly, rate, capYears, fromEob } = appliedCapTooltipInputs(month, terms);

    const intYear = Math.floor(month.context.policyYear);
    const cap = capYears && capYears > 0 ? capYears : null;
    // Exponent = policyYear − 1, capped at capYears, matching the engine's
    // `compoundFactor` so the tooltip's base × (1+r)^n equals the displayed result.
    const rawIncrements = Math.max(intYear - 1, 0);
    const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
    const capped = cap !== null && intYear > cap;
    const poolLabel = fromEob ? 'EOB' : 'Acceleration';

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="leading-tight cursor-help">
            <div className="font-medium text-gray-900">{currency(monthlyCap)}</div>
            <div className="text-[10px] text-gray-500">
              {currency(monthlyCap / DAYS_IN_MONTH)}/day
            </div>
          </div>
        </TooltipTrigger>
        <InflationTooltipContent
          header={`Policy year ${intYear} · ${poolLabel} monthly limit`}
          startingLabel="At issue"
          startingValue={baseMonthly}
          rate={rate}
          years={increments}
          result={monthlyCap}
          note={capped ? `ACIO capped at year ${cap}` : undefined}
          compoundLabel="Compound ACIO growth"
          footer={poolCeilingFooter(monthlyCap, terms)}
        />
      </Tooltip>
    );
  },

  benefitAmountValueForMonth: ({ month }) =>
    month.events.ineligibilityReason === 'pre-policy'
      ? undefined
      : month.facts.appliedMonthlyBenefitCap,

  poolCellTooltip: ({ month, openState, terms }) => {
    const openBase = openState.pools.baseBenefitPool;
    const openEob = openState.pools.extensionOfBenefitsPool;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    const baseDepletion = month.deltas.base.depletion;
    const eobDepletion = month.deltas.eob.depletion;
    const baseAccrual = month.deltas.base.inflationAccrual;
    const eobAccrual = month.deltas.eob.inflationAccrual;

    const allZero =
      openBase === 0 &&
      openEob === 0 &&
      closeBase === 0 &&
      closeEob === 0 &&
      baseDepletion === 0 &&
      eobDepletion === 0 &&
      baseAccrual === 0 &&
      eobAccrual === 0;
    if (allZero) return null;

    const eobUntouched = openEob > 0 && closeEob === openEob && eobAccrual === 0;

    const baseMonthly = terms.nominalDailyBenefitBase * DAYS_IN_MONTH;
    const policyYear = month.context.policyYear;
    const baseAtIssueNote = poolAtIssueNote(
      baseMonthly,
      terms.baseBenefitPoolDurationMonths,
      terms.baseBenefitPoolAtIssue,
    );
    const eobAtIssueNote = poolAtIssueNote(
      baseMonthly,
      terms.extensionOfBenefitsPoolDurationMonths,
      terms.extensionOfBenefitsPoolAtIssue,
    );
    const eobNoteParts = [
      eobAtIssueNote,
      eobUntouched ? 'Not yet engaged' : undefined,
    ].filter(Boolean);

    return (
      <BalanceBreakdownTooltip
        title="LTC pool breakdown"
        description="Asset Flex pays from the Acceleration Benefit first; the Extension of Benefits pool takes over once Acceleration is exhausted. ACIO grows both pools at the policy anniversary."
        sections={[
          {
            heading: `Acceleration Benefit · Policy year ${policyYear}`,
            opening: openBase,
            closing: closeBase,
            lines: [
              { label: 'ACIO accrual', value: baseAccrual, kind: 'add' },
              { label: 'Drawn this month', value: baseDepletion, kind: 'subtract' },
            ],
            note: baseAtIssueNote,
          },
          {
            heading: `Extension of Benefits · Policy year ${policyYear}`,
            opening: openEob,
            closing: closeEob,
            lines: [
              { label: 'ACIO accrual', value: eobAccrual, kind: 'add' },
              { label: 'Drawn this month', value: eobDepletion, kind: 'subtract' },
            ],
            note: eobNoteParts.length > 0 ? eobNoteParts.join(' · ') : undefined,
          },
        ]}
        footer="Pool dollars are tracked through reimbursement and inflated by ACIO each anniversary. May diverge slightly from the carrier illustration's printed forward-inflated pool totals."
      />
    );
  },

  deathBenefitCellTooltip: ({ month, openState }) => {
    const open = openState.pools.deathBenefit;
    const close = month.state.pools.deathBenefit;
    const reduction = month.deltas.deathBenefit.reduction;
    const flooredAtMinimum = month.deltas.deathBenefit.flooredAtMinimum;
    const accelDrawdown = month.deltas.base.depletion;
    if (open === 0 && close === 0 && reduction === 0) return null;

    // When the Residual floor capped the reduction, the raw Acceleration
    // drawdown is bigger than the actual DB depletion. Surface that gap so
    // the advisor sees why Opening − Drawn ≠ Closing in this row.
    const floorNote = flooredAtMinimum
      ? `Acceleration drew ${currency(accelDrawdown)}; depletion capped at the Residual Death Benefit (${currency(close)}).`
      : undefined;

    return (
      <BalanceBreakdownTooltip
        title="Death benefit"
        description="Flat Face Amount, depleted dollar-for-dollar by Acceleration Benefit drawdown. EOB drawdown does NOT deplete the death benefit. Floors at the Residual Death Benefit."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [
              {
                label: 'Reduced this month',
                value: reduction,
                kind: 'subtract',
              },
            ],
            note: floorNote,
          },
        ]}
      />
    );
  },

  payoutCellTooltip: ({ month }) => {
    const cost = month.facts.effectiveCareCost;
    const eligibleDays = month.facts.coverageEligibleDays;
    const dailyCost = month.facts.dailyCareCost;
    const paid = month.deltas.payout.ltcCoveragePaid;
    const drawnBase = month.deltas.base.depletion;
    const drawnEob = month.deltas.eob.depletion;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    if (eligibleDays === 0) return null;

    // Reimbursement is computed against pro-rated cost (eligibleDays ×
    // dailyCareCost), not the full-month cost. Surface that distinction so
    // partial-eligibility months don't mislabel the cost basis or
    // overstate OOP attribution to "policy didn't cover".
    const isPartial = eligibleDays < DAYS_IN_MONTH;
    const eligibleCost = isPartial ? eligibleDays * dailyCost : cost;
    const ineligibleDayCost = Math.max(cost - eligibleCost, 0);
    const eligibleOop = Math.max(eligibleCost - paid, 0);

    let rule: string;
    if (paid === 0 && closeBase === 0 && closeEob === 0) {
      rule = 'Both LTC pools are exhausted — the policy reimburses nothing; the full care cost is out-of-pocket.';
    } else if (paid >= eligibleCost - 0.01) {
      rule = 'Care cost is at or below the monthly benefit cap. Since Asset Flex is a reimbursement product, the policy pays the actual eligible cost.';
    } else if (paid > 0) {
      rule = 'Care cost exceeds the monthly benefit cap. Asset Flex reimburses up to the cap; the excess is out-of-pocket.';
    } else {
      rule = 'No reimbursement this month.';
    }

    const drawnFrom: string[] = [];
    if (drawnBase > 0) drawnFrom.push(`Acceleration ${currency(drawnBase)}`);
    if (drawnEob > 0) drawnFrom.push(`Extension of Benefits ${currency(drawnEob)}`);

    const proratedNote = isPartial && paid > 0
      ? `Pro-rated for ${eligibleDays} eligible day${eligibleDays === 1 ? '' : 's'} (waiting period or <2 ADL consumed the rest of the month).`
      : null;

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — reimbursement</div>
        <div className="mb-1 text-[10px] text-gray-300">{rule}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <span className="text-gray-300">Care cost this month</span>
          <span className="font-semibold tabular-nums">{currency(cost)}</span>
          {isPartial && (
            <>
              <span className="text-gray-300">Eligible portion</span>
              <span className="font-semibold tabular-nums">
                {currency(eligibleCost)}
                <span className="ml-1 font-normal text-gray-400">
                  ({eligibleDays}d × {currency(dailyCost)})
                </span>
              </span>
            </>
          )}
          <span className="text-gray-300">Reimbursed</span>
          <span className="font-semibold tabular-nums">{currency(paid)}</span>
          {eligibleOop > 0 && (
            <>
              <span className="text-gray-300">
                {isPartial ? 'OOP (over cap)' : 'Out-of-pocket'}
              </span>
              <span className="font-semibold tabular-nums text-rose-300">
                {currency(eligibleOop)}
              </span>
            </>
          )}
          {ineligibleDayCost > 0 && (
            <>
              <span className="text-gray-300">OOP (ineligible days)</span>
              <span className="font-semibold tabular-nums text-rose-300">
                {currency(ineligibleDayCost)}
              </span>
            </>
          )}
        </div>
        {drawnFrom.length > 0 && (
          <div className="mt-1 text-[10px] text-gray-400">
            Drawn from: {drawnFrom.join(' + ')}
          </div>
        )}
        {proratedNote && (
          <div className="mt-1 text-[10px] text-gray-400">{proratedNote}</div>
        )}
      </TooltipContent>
    );
  },

  // Asset Flex's monthly cap inflates by ACIO on the On-Face amount
  // (`baseBenefitPoolInflationRate`), which can differ from the policy-wide
  // ACIO when an On-Face override is set. Use that rate in the tooltip so
  // the `base × (1+r)^n` math reconciles with the displayed cap.
  benefitInflationRate: terms =>
    terms.baseBenefitPoolInflationRate ?? terms.inflationRate,

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC pool balance. Asset Flex draws from the Acceleration Benefit (AOB) first, then the Extension of Benefits (EOB) pool. ACIO grows both pools at each policy anniversary."
        formulas={[
          { label: 'Closing pool', expression: '(opening AOB + opening EOB) + ACIO accrual − drawn this month' },
          { label: 'Draw order', expression: 'AOB first; EOB engages once AOB is exhausted' },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Reimbursement payout this month, with cumulative dollars paid by the policy through this month in parentheses."
        formulas={[
          { label: 'Monthly payout', expression: 'min(eligible cost, monthly benefit cap, pool remaining)' },
          { label: 'Eligible cost', expression: 'daily care cost × eligible days (waiting period / <2 ADL excluded)' },
        ]}
        notes="Partial-eligibility months are pro-rated by eligible days; OOP can come from over-cap or ineligible days."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost × <GrowthFactor rate="care-cost inflation" exponent="years from today" />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost × days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly benefit maximum (cap) for this policy year, with the daily equivalent beneath. Asset Flex inflates the cap by On-Face ACIO."
        formulas={[
          {
            label: 'Monthly cap',
            expression: (
              <>
                at-issue monthly × <GrowthFactor rate="ACIO rate" exponent="policy year − 1" />
              </>
            ),
          },
          { label: 'Pool ceiling', expression: 'monthly cap × duration months (AOB or EOB)' },
        ]}
        notes="ACIO capped at the configured cap year if specified. On-Face ACIO can differ from policy-wide ACIO."
      />
    ),
    poolLimitTooltip: (
      <HeaderTooltip
        description="Inflated AOB + EOB pool sizes at this policy year if no benefits were used. Pools keep growing on each future anniversary until any ACIO cap is reached. Distinct from Pool remaining, which is the actual balance after payouts."
        formulas={[
          {
            label: 'AOB',
            expression: (
              <>
                AOB at issue × <GrowthFactor rate="ACIO rate" exponent="policy year − 1" />
              </>
            ),
          },
          {
            label: 'EOB',
            expression: (
              <>
                EOB at issue × <GrowthFactor rate="ACIO rate" exponent="policy year − 1" />
              </>
            ),
          },
          { label: 'Total', expression: 'AOB value + EOB value' },
        ]}
        notes="Each pool capped at its own ACIO cap year if specified — once capped, the value is a true ceiling."
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative reduction of the AOB + EOB pools through this month — any utilization before the projection plus this projection's drawdown."
        formulas={[
          { label: 'Drawn', expression: 'prior utilization + Σ (AOB depletion + EOB depletion)' },
          { label: 'Identity', expression: 'Pool limit − Drawn = Pool remaining (while pool is open)' },
        ]}
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month death benefit, with this month's change beneath. Asset Flex depletes the death benefit dollar-for-dollar by Acceleration drawdown; EOB drawdown does not."
        formulas={[
          { label: 'Death benefit', expression: 'at-issue Face Amount − Σ (AOB drawdown)' },
          { label: 'Floor', expression: 'max(above, Residual Death Benefit)' },
        ]}
      />
    ),
    benefitInflationCompoundLabel: 'Compound ACIO growth',
    benefitInflationCapNote: capYear => `ACIO capped at year ${capYear}`,
  },
};
